import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import type { AvailabilityMap, AvailabilityStatus, Contributor, ContributorWeekSchedule, Solution, WeekSchedulePayload } from '../types';
import { StatusToggle } from './StatusToggle';
import { resolveEffectiveStatus } from '../utils/availabilityResolver';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function getDatesForWeek(weekStart: string): string[] {
  const dates: string[] = [];
  const start = new Date(weekStart + 'T00:00:00Z');
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}
function cycleStatus(current: AvailabilityStatus): AvailabilityStatus {
  const cycle: AvailabilityStatus[] = ['available', 'limited', 'unavailable'];
  return cycle[(cycle.indexOf(current) + 1) % cycle.length];
}

// Mirrors the OOO check in AvailabilityGrid but for any date, not just today.
function isDateOOO(date: string, contributor: Contributor): boolean {
  if (contributor.oooStartDate && contributor.oooEndDate) {
    return contributor.oooStartDate <= date && contributor.oooEndDate >= date;
  }
  return !!contributor.isOutOfOffice;
}
interface Props {
  contributor: Contributor;
  solutions: Solution[];
  baseAvailability: AvailabilityMap;
  canEdit: boolean;
  onClose: () => void;
  fetchCurrentWeek: (contributorId: string) => Promise<WeekSchedulePayload | null>;
  fetchNextWeek: (contributorId: string) => Promise<WeekSchedulePayload | null>;
  onUpdateDailyAvailability: (solutionId: string, date: string, status: AvailabilityStatus | null) => Promise<void>;
  /** true (default) = two-tab slide-over panel; false = single 14-column inline grid */
  showTabs?: boolean;
}
export function ContributorWeekView({
  contributor,
  solutions,
  baseAvailability,
  canEdit,
  onClose,
  fetchCurrentWeek,
  fetchNextWeek,
  onUpdateDailyAvailability,
  showTabs = true
}: Props) {
  const [currentWeek, setCurrentWeek] = useState<WeekSchedulePayload | null>(null);
  const [nextWeek, setNextWeek] = useState<WeekSchedulePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [markingSolutions, setMarkingSolutions] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'current' | 'next'>('current');
  const today = new Date().toISOString().split('T')[0];
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCurrentWeek(contributor.id), fetchNextWeek(contributor.id)]).then(([cw, nw]) => {
      setCurrentWeek(cw);
      setNextWeek(nw);
      setLoading(false);
    });
  }, [contributor.id]);
  const activeWeek = activeTab === 'current' ? currentWeek : nextWeek;
  const setActiveWeek = activeTab === 'current' ? setCurrentWeek : setNextWeek;
  const handleCellClick = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string, date: string) => {
    if (!canEdit) return;
    const key = `${date}_${solutionId}`;
    setUpdating(key);
    const currentStatus = resolveEffectiveStatus(contributor.id, solutionId, {
      [contributor.id]: weekPayload.schedule[date] ?? {}
    }, baseAvailability) ?? 'unavailable';
    const newStatus = cycleStatus(currentStatus);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule,
        [date]: {
          ...(prev.schedule[date] ?? {}),
          [solutionId]: newStatus
        }
      };
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    await onUpdateDailyAvailability(solutionId, date, newStatus);
    setUpdating(null);
  };

  // Bulk-sets all 7 days of a single solution's row to `status` for the given week.
  const handleMarkSolutionWeek = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string, status: AvailabilityStatus) => {
    if (!canEdit) return;
    const markKey = `${weekPayload.weekKey}_${solutionId}`;
    if (markingSolutions[markKey]) return;
    setMarkingSolutions(prev => ({
      ...prev,
      [markKey]: true
    }));

    // Only weekdays (Mon-Fri) — Sat/Sun are excluded from bulk week updates.
    const dates = getDatesForWeek(weekPayload.weekStart).slice(0, 5);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule
      };
      for (const date of dates) {
        updatedSchedule[date] = {
          ...(updatedSchedule[date] ?? {}),
          [solutionId]: status
        };
      }
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    try {
      await Promise.all(dates.map(date => onUpdateDailyAvailability(solutionId, date, status)));
    } finally {
      setMarkingSolutions(prev => {
        const next = {
          ...prev
        };
        delete next[markKey];
        return next;
      });
    }
  };

  // Clears any per-day weekly overrides across all 7 days of the given week,
  // restoring each day to whatever the base/date-range resolution would show.
  const handleClearSolutionWeek = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string) => {
    if (!canEdit) return;
    const markKey = `${weekPayload.weekKey}_${solutionId}`;
    if (markingSolutions[markKey]) return;
    setMarkingSolutions(prev => ({
      ...prev,
      [markKey]: true
    }));
    const dates = getDatesForWeek(weekPayload.weekStart);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule
      };
      for (const date of dates) {
        if (!updatedSchedule[date] || updatedSchedule[date][solutionId] === undefined) continue;
        const {
          [solutionId]: _removed,
          ...rest
        } = updatedSchedule[date];
        updatedSchedule[date] = rest;
      }
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    try {
      await Promise.all(dates.map(date => onUpdateDailyAvailability(solutionId, date, null)));
    } finally {
      setMarkingSolutions(prev => {
        const next = {
          ...prev
        };
        delete next[markKey];
        return next;
      });
    }
  };

  // Used by the tabbed view (slide-over panel)
  const renderWeekGrid = (weekPayload: WeekSchedulePayload | null, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>) => {
    if (!weekPayload) {
      return <div className="p-4 text-slate-500 text-sm" data-id="element-53" data-mp="1p1qinqlh67sj">Failed to load week schedule.</div>;
    }
    const dates = getDatesForWeek(weekPayload.weekStart);
    return <table className="w-full text-xs border-collapse" data-id="element-54" data-mp="2hrqts76kkh3u">
        <thead data-id="element-55" data-mp="2hq5xod4lwq3v">
          <tr data-id="element-56" data-mp="2hpmfcvb1nzpb">
            <th className="text-left py-2 px-3 text-slate-500 font-medium w-48 min-w-[160px]" data-id="element-57" data-mp="2p2lpzweot8kl">
              Solution
            </th>
            {dates.map((date, i) => {
            const isToday = date === today;
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            return <th key={date} className={`text-center py-2 px-2 font-medium w-16 ${isOOO ? 'bg-slate-100 text-slate-400' : isToday ? 'text-[#EB1000]' : isWeekend ? 'bg-slate-100 text-slate-400' : 'text-slate-500'}`} data-id="element-58" data-mp="33367mp8v8yci" data-mpi={i}>
                  <div data-id="element-59" data-mp="3ms6u86s9sw0p">{DAY_LABELS[i]}</div>
                  <div className={`text-[10px] font-normal mt-0.5 ${isToday && !isOOO ? 'font-semibold' : ''}`} data-id="element-60" data-mp="3qt0jb3hg8gv6">
                    {date.slice(5)}
                  </div>
                  {isToday && !isOOO && <div className="mx-auto mt-1 h-1 w-1 rounded-full bg-[#EB1000]" data-id="element-61" data-mp="kvti3urb2oi4" />}
                  {isOOO && <div className="text-[9px] mt-0.5 font-medium" data-id="element-62" data-mp="fyb8011jy38j">OOO</div>}
                </th>;
          })}
          </tr>
        </thead>
        <tbody data-id="element-63" data-mp="usptc88l9oqd">
          {solutions.map(solution => <tr key={solution.id} className="group border-t border-slate-100 hover:bg-slate-50/50 transition-colors" data-id="element-64" data-mp="pwr30f3p9wnb">
              <td className="py-2 px-3 font-medium max-w-[160px]" data-id="element-65" data-mp="26mvvxrazerk">
                <div className="flex flex-col gap-0.5" data-id="element-66" data-mp="3td7mxhhcymol">
                  <span className="text-slate-700 truncate" data-id="element-67" data-mp="3talfdhugw76d">{solution.name}</span>
                  {canEdit && <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1" data-id="element-68" data-mp="75hr5z6givsk">
                      {markingSolutions[`${weekPayload.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-69" data-mp="3am6m0nwogh2e" /> : <>
                          {(['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(weekPayload, setWeekFn, solution.id, s)} title={`Mark whole week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-70" data-mp="1uw75w717ysxh" />)}
                          <button onClick={() => handleClearSolutionWeek(weekPayload, setWeekFn, solution.id)} title="Clear weekly overrides" className="h-3.5 w-3.5 rounded-full flex-shrink-0 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-71" data-mp="ocz5i5fio07n">
                            <X className="h-3 w-3" data-id="element-72" data-mp="1a5s4irzbih85" />
                          </button>
                        </>}
                    </div>}
                </div>
              </td>
              {dates.map((date, i) => {
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = weekPayload.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(contributor.id, solution.id, weeklyScheduleForDate, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            const hasWeeklyOverride = weeklyOverride !== undefined;
            return <td key={date} className={`py-2 px-2 text-center ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-100' : ''}`} data-id="element-73" data-mp="nrcrgif6uvsx" data-mpi={i}>
                    <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : isWeekend ? 'opacity-60' : ''}`} data-id="element-74" data-mp="xn69uifl9jim">
                      {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-75" data-mp="16odwg38qsvwv" /> : <StatusToggle status={status} onClick={() => handleCellClick(weekPayload, setWeekFn, solution.id, date)} disabled={!canEdit || isOOO || isWeekend} data-id="element-76" data-mp="11pj5uf4ja327" />}
                      {hasWeeklyOverride && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-77" data-mp="17fm678e83wuy" />}
                    </div>
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>;
  };

  // Used by the inline grid view (name click) — all 14 dates in one horizontally-scrollable table
  const renderCombinedGrid = (cw: WeekSchedulePayload | null, nw: WeekSchedulePayload | null) => {
    const currentDates = cw ? getDatesForWeek(cw.weekStart) : [];
    const nextDates = nw ? getDatesForWeek(nw.weekStart) : [];
    const renderDateTh = (date: string, i: number, isFirstOfNextWeek: boolean) => {
      const isToday = date === today;
      const isWeekend = i >= 5;
      const isOOO = isDateOOO(date, contributor);
      return <th key={date} className={`text-center py-2 px-1 font-medium ${isFirstOfNextWeek ? 'border-l-2 border-slate-300' : ''} ${isOOO ? 'bg-slate-100 text-slate-400' : isToday ? 'text-[#EB1000]' : isWeekend ? 'bg-slate-100 text-slate-400' : 'text-slate-500'}`} data-id="element-78" data-mp="2ecp9g3rhe2r">
          <div data-id="element-79" data-mp="l418iby9362a">{DAY_LABELS[i]}</div>
          <div className={`text-[10px] font-normal mt-0.5 ${isToday && !isOOO ? 'font-semibold' : ''}`} data-id="element-80" data-mp="l76wfnv581wf">
            {date.slice(5)}
          </div>
          {isToday && !isOOO && <div className="mx-auto mt-1 h-1 w-1 rounded-full bg-[#EB1000]" data-id="element-81" data-mp="cbhfbr0q9tou" />}
          {isOOO && <div className="text-[9px] mt-0.5 font-medium" data-id="element-82" data-mp="7cmoq2wir0u6">OOO</div>}
        </th>;
    };
    return <table className="w-full table-fixed text-xs border-collapse" data-id="element-83" data-mp="14tv5r5jqiddw">
          <thead data-id="element-84" data-mp="101tmgzo5fmed">
            {/* Week group labels */}
            <tr data-id="element-85" data-mp="100a4n4muh3ah">
              <th className="py-1.5 px-3" style={{
            width: '160px'
          }} data-id="element-86" data-mp="jmu6b0j5bb52" />
              <th colSpan={7} className="text-center py-1.5 text-[11px] font-semibold text-slate-600 border-b border-slate-100" data-id="element-87" data-mp="jlj24zlsg1df">
                This Week
                {cw && <span className="ml-1.5 font-normal text-slate-400" data-id="element-88" data-mp="fl8yhtlgxn1q">
                    {cw.weekStart.slice(5)} – {cw.weekEnd.slice(5)}
                  </span>}
              </th>
              <th colSpan={7} className="text-center py-1.5 text-[11px] font-semibold text-slate-600 border-b border-slate-100 border-l-2 border-l-slate-300" data-id="element-89" data-mp="17cf1qiwhk0v8">
                Next Week
                {nw && <span className="ml-1.5 font-normal text-slate-400" data-id="element-90" data-mp="yijy5fb0iihp">
                    {nw.weekStart.slice(5)} – {nw.weekEnd.slice(5)}
                  </span>}
              </th>
            </tr>
            {/* Day headers */}
            <tr data-id="element-91" data-mp="20ezdh50udkiw">
              <th className="text-left py-2 px-3 text-slate-500 font-medium" style={{
            width: '160px'
          }} data-id="element-92" data-mp="20efvjob6yw4v">
                Solution
              </th>
              {currentDates.map((date, i) => renderDateTh(date, i, false))}
              {nextDates.map((date, i) => renderDateTh(date, i, i === 0))}
            </tr>
          </thead>
          <tbody data-id="element-93" data-mp="256heft2v7l3v">
            {solutions.map(solution => <tr key={solution.id} className="group border-t border-slate-100 hover:bg-slate-50/50 transition-colors" data-id="element-94" data-mp="1hftweez8jf3l">
                <td className="py-2 px-3 font-medium" style={{
            width: '160px'
          }} data-id="element-95" data-mp="1dg37kgt9r91p">
                  <div className="flex flex-col gap-0.5" data-id="element-96" data-mp="1ret33s0fx0ny">
                    <span className="text-slate-700 truncate" data-id="element-97" data-mp="1rc6mz3zhhcu4">{solution.name}</span>
                    {canEdit && <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-0.5" data-id="element-98" data-mp="32tg5noasg0ud">
                        {cw && <div className="flex items-center gap-0.5" data-id="element-99" data-mp="2xu3g2vsj8fo3">
                            <span className="text-[9px] text-slate-400 shrink-0 mr-0.5" data-id="element-100" data-mp="35w2svf0xpnj8">CW:</span>
                            {markingSolutions[`${cw.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-101" data-mp="3evhg85lt1hzi" /> : <>
                                {(['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(cw, setCurrentWeek, solution.id, s)} title={`Mark current week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-102" data-mp="2m5h69scg5eps" />)}
                                <button onClick={() => handleClearSolutionWeek(cw, setCurrentWeek, solution.id)} title="Clear current week overrides" className="h-3.5 w-3.5 rounded-full flex-shrink-0 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-103" data-mp="1qwx2r0h3ijvk">
                                  <X className="h-3 w-3" data-id="element-104" data-mp="185e0wwyt5bv4" />
                                </button>
                              </>}
                          </div>}
                        {nw && <div className="flex items-center gap-0.5" data-id="element-105" data-mp="u90oi294s0ni">
                            <span className="text-[9px] text-slate-400 shrink-0 mr-0.5" data-id="element-106" data-mp="2i4438i40y9g8">NW:</span>
                            {markingSolutions[`${nw.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-107" data-mp="286hmrr72u9u5" /> : <>
                                {(['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(nw, setNextWeek, solution.id, s)} title={`Mark next week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-108" data-mp="1zcuuib22vopp" />)}
                                <button onClick={() => handleClearSolutionWeek(nw, setNextWeek, solution.id)} title="Clear next week overrides" className="h-3.5 w-3.5 rounded-full flex-shrink-0 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-109" data-mp="1atjc3pz28ujx">
                                  <X className="h-3 w-3" data-id="element-110" data-mp="1tj964av01cas" />
                                </button>
                              </>}
                          </div>}
                      </div>}
                  </div>
                </td>
                {/* Current week cells */}
                {currentDates.map((date, i) => {
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = cw?.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(contributor.id, solution.id, weeklyScheduleForDate, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            return <td key={date} className={`py-2 px-2 text-center ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-100' : ''}`} data-id="element-111" data-mp="1x8vyoo7sn065" data-mpi={i}>
                      <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : isWeekend ? 'opacity-60' : ''}`} data-id="element-112" data-mp="14ljcwg9giwdw">
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-113" data-mp="3g6s9gaewvt01" /> : <StatusToggle status={status} onClick={() => cw && handleCellClick(cw, setCurrentWeek, solution.id, date)} disabled={!canEdit || !cw || isOOO || isWeekend} data-id="element-114" data-mp="36ap7b4k27ud3" />}
                        {weeklyOverride !== undefined && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-115" data-mp="3pyy8g43kzaoq" />}
                      </div>
                    </td>;
          })}
                {/* Next week cells */}
                {nextDates.map((date, i) => {
            const isWeekend = i >= 5;
            const isFirstCell = i === 0;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = nw?.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(contributor.id, solution.id, weeklyScheduleForDate, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            return <td key={date} className={`py-2 px-2 text-center ${isFirstCell ? 'border-l-2 border-slate-300' : ''} ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-100' : ''}`} data-id="element-116" data-mp="3skxjttamtpec" data-mpi={i}>
                      <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : isWeekend ? 'opacity-60' : ''}`} data-id="element-117" data-mp="1ltppz68g91zv">
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-118" data-mp="1yaqpbny1icqh" /> : <StatusToggle status={status} onClick={() => nw && handleCellClick(nw, setNextWeek, solution.id, date)} disabled={!canEdit || !nw || isOOO || isWeekend} data-id="element-119" data-mp="2330ciw7rsyp2" />}
                        {weeklyOverride !== undefined && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-120" data-mp="1kafygkwqcy5b" />}
                      </div>
                    </td>;
          })}
              </tr>)}
          </tbody>
        </table>;
  };
  const legend = <p className="mt-3 text-xs text-slate-400" data-id="element-121" data-mp="30nwei9md7pbw">
      <span className="inline-flex items-center gap-1" data-id="element-122" data-mp="dgrymnya5fzp">
        <span className="h-1.5 w-1.5 rounded-full bg-blue-400 inline-block" data-id="element-123" data-mp="dje4mj5ivnft" />
        Weekly override set &nbsp;·&nbsp; Click a circle to cycle: available → limited → unavailable
      </span>
    </p>;
  if (loading) {
    return <div className="flex items-center justify-center p-12" data-id="element-124" data-mp="hj54rdgxvfx0">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" data-id="element-125" data-mp="hkzo4nckazyz" />
        <span className="ml-2 text-slate-500 text-sm" data-id="element-126" data-mp="weusph5oq34d">Loading week schedule…</span>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-127" data-mp="rgjgizrx3giz">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white" data-id="element-128" data-mp="158plrqtj833n">
        <div data-id="element-129" data-mp="116mbv6mih6kh">
          <h2 className="text-base font-semibold text-slate-900" data-id="element-130" data-mp="1185o8m51qxgz">{contributor.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-131" data-mp="1f7oxxjdiagve">Weekly availability — {contributor.region}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Close week view" data-id="element-132" data-mp="1a8vllu9ygvwt">
          <X className="h-4 w-4" data-id="element-133" data-mp="1yrx9cegzguqk" />
        </button>
      </div>

      {showTabs ? <>
          {/* Week tabs */}
          <div className="flex border-b border-slate-200 bg-white px-6" data-id="element-134" data-mp="1jxiwywrxcjkv">
            {(['current', 'next'] as const).map(tab => {
          const week = tab === 'current' ? currentWeek : nextWeek;
          const label = tab === 'current' ? 'This Week' : 'Next Week';
          const range = week ? `${week.weekStart.slice(5)} – ${week.weekEnd.slice(5)}` : '';
          return <button key={tab} onClick={() => setActiveTab(tab)} className={`mr-6 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-[#EB1000] text-[#EB1000]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-135" data-mp="1638gt3tztxqf">
                  {label}
                  {range && <span className="ml-1.5 text-xs font-normal opacity-70" data-id="element-136" data-mp="2u07rxx65c8ly">{range}</span>}
                </button>;
        })}
          </div>
          <div className="overflow-auto flex-1 p-4" data-id="element-137" data-mp="2k44kcbsizhrm">
            {activeWeek ? renderWeekGrid(activeWeek, setActiveWeek) : <div className="p-4 text-slate-500 text-sm" data-id="element-138" data-mp="3hqbvk4jc48cy">Failed to load week schedule.</div>}
            {legend}
          </div>
        </> : (/* Single 14-column pane — horizontally scrollable */
    <div className="overflow-auto flex-1 p-4" data-id="element-139" data-mp="37us53xnji1vl">
          {renderCombinedGrid(currentWeek, nextWeek)}
          {legend}
        </div>)}
    </div>;
}